import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import type {
  CreateAgentConfigurationVersionResponse,
  GetAgentConfigurationVersionResponse,
  GetAgentConfigurationVersionsRequest,
  GetAgentConfigurationVersionsResponse,
  UpdateAgentConfigurationVersionResponse,
} from '@mai/platform-api/src/routes/agent-configuration-versions.schema';
import type {
  AgentConfigurationVersion,
  CreateAgentConfigurationVersion as CreateAgentConfigurationVersionType,
  UpdateAgentConfigurationVersion as UpdateAgentConfigurationVersionType,
} from '@mai/types';

import { type QueryOptions, apiClient } from './axios';

// Type definitions from platform API schemas
export type GetAgentConfigurationVersionsRequestType =
  GetAgentConfigurationVersionsRequest;
export type CreateAgentConfigurationVersionRequest =
  CreateAgentConfigurationVersionType;
export type UpdateAgentConfigurationVersionRequestType =
  UpdateAgentConfigurationVersionType;
export type {
  AgentConfigurationVersion,
  CreateAgentConfigurationVersionResponse,
  UpdateAgentConfigurationVersionResponse,
};

// API functions
export const fetchAgentConfigurationVersions = async (
  params?: GetAgentConfigurationVersionsRequestType,
): Promise<GetAgentConfigurationVersionsResponse> => {
  const response = await apiClient.get('/agent-configuration-versions', {
    params,
  });
  return response.data;
};

export const fetchAgentConfigurationVersionsByAgentId = async (
  agentId: string,
): Promise<GetAgentConfigurationVersionsResponse> => {
  return fetchAgentConfigurationVersions({ agentIds: [agentId] });
};

export const fetchAgentConfigurationVersionById = async (
  versionId: string,
): Promise<GetAgentConfigurationVersionResponse> => {
  const response = await apiClient.get(
    `/agent-configuration-versions/${versionId}`,
  );
  return response.data;
};

export const createAgentConfigurationVersion = async (
  data: CreateAgentConfigurationVersionRequest,
): Promise<CreateAgentConfigurationVersionResponse> => {
  const response = await apiClient.post('/agent-configuration-versions', data);
  return response.data;
};

export const updateAgentConfigurationVersion = async (
  versionId: string,
  data: UpdateAgentConfigurationVersionRequestType,
): Promise<UpdateAgentConfigurationVersionResponse> => {
  const response = await apiClient.patch(
    `/agent-configuration-versions/${versionId}`,
    data,
  );
  return response.data;
};

// React Query hooks
export const useAgentConfigurationVersions = (
  params?: GetAgentConfigurationVersionsRequestType,
  options?: QueryOptions,
) => {
  return useQuery({
    queryKey: ['agent-configuration-versions', params],
    queryFn: () => fetchAgentConfigurationVersions(params),
    enabled: options?.enabled,
    retry: options?.retry,
  });
};

export const useAgentConfigurationVersionsByAgentId = (
  agentId: string | null,
  options?: QueryOptions,
) => {
  return useQuery({
    queryKey: ['agent-configuration-versions', 'by-agent', agentId],
    queryFn: () => fetchAgentConfigurationVersionsByAgentId(agentId!),
    enabled: !!agentId && options?.enabled !== false,
    retry: options?.retry,
  });
};

export const useAgentConfigurationVersionById = (
  versionId: string | null,
  options?: QueryOptions,
) => {
  return useQuery({
    queryKey: ['agent-configuration-versions', 'by-id', versionId],
    queryFn: () => fetchAgentConfigurationVersionById(versionId!),
    enabled: !!versionId && options?.enabled !== false,
    retry: options?.retry,
  });
};

export const useCreateAgentConfigurationVersion = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createAgentConfigurationVersion,
    onSuccess: () => {
      // Invalidate all agent configuration version queries to refetch the data
      void queryClient.invalidateQueries({
        queryKey: ['agent-configuration-versions'],
      });
      // Also invalidate agent queries since this affects current configuration
      void queryClient.invalidateQueries({ queryKey: ['agents'] });
    },
  });
};

export const useUpdateAgentConfigurationVersion = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      versionId,
      data,
    }: {
      versionId: string;
      data: UpdateAgentConfigurationVersionRequestType;
    }) => updateAgentConfigurationVersion(versionId, data),
    onSuccess: () => {
      // Invalidate all agent configuration version queries to refetch the data
      void queryClient.invalidateQueries({
        queryKey: ['agent-configuration-versions'],
      });
      // Also invalidate agent queries since this affects current configuration
      void queryClient.invalidateQueries({ queryKey: ['agents'] });
    },
  });
};
